import { useEffect, useRef } from "react";
import {
  createDrawing,
  loadDrawings,
  removeStaleOverlays,
  syncDrawings,
  type DrawingSyncStateMap,
} from "../chart-drawings";
import { isFutureAnchored } from "../drawings-KLine/overlay-utils";
import { registerAllCustomOverlays } from "../kline-helpers";
import { requiresTextPrompt } from "@/lib/chart-drawings/registry";
import type { DrawingInstance } from "@/lib/chart-drawings/types";

interface UseChartDrawingsProps {
  chartInstanceRef: React.MutableRefObject<{ removeOverlay: (id: string) => void } | null>;
  selectedDrawingType: string | null;
  selectedSymbol: { currency: string } | null;
  selectedTimeframe?: string;
  chartPanelId?: string;
  drawings: DrawingInstance[];
  addDrawing: (object: Omit<DrawingInstance, "addedAt">) => void;
  updateDrawingSettings: (id: string, settings: Record<string, unknown>) => void;
  getDrawingsBySymbol: (symbol: string) => DrawingInstance[];
  setShowTextModal: (show: boolean) => void;
  setTextValue: (value: string) => void;
  onDrawingAdded: () => void;
  /**
   * Monotonic counter that bumps whenever new candle data is applied to the
   * chart. We need it because future-anchored points can only be resolved
   * once `chart.getDataList()` is populated; a fresh page load schedules
   * `loadDrawings` before the async OHLC fetch completes, so without this
   * signal future points get rendered with no timing data and clamp to the
   * last (still empty) candle.
   */
  dataRevision?: number;
}

export const useChartDrawings = ({
  chartInstanceRef,
  selectedDrawingType,
  selectedSymbol,
  selectedTimeframe,
  chartPanelId,
  drawings,
  addDrawing,
  updateDrawingSettings,
  getDrawingsBySymbol,
  setShowTextModal,
  setTextValue,
  onDrawingAdded,
  dataRevision,
}: UseChartDrawingsProps) => {
  const trackedOverlayIdsRef = useRef<Set<string>>(new Set());
  const syncStateMapRef = useRef<DrawingSyncStateMap>(new Map());

  useEffect(() => {
    registerAllCustomOverlays();
  }, []);

  // Draw-mode arming (DR-06 + DR-11). Entering draw mode requires both a live
  // chart instance and a selected symbol — without a symbol the created
  // instance would be persisted with an empty `symbolKey` and orphaned
  // (DR-06). Refs are not reactive, so `chartInstanceRef.current` may still be
  // null on the render where `selectedDrawingType` is set; we therefore also
  // depend on `dataRevision` (a chart-ready signal) so the effect re-runs and
  // retries arming once the chart becomes available (DR-11).
  useEffect(() => {
    if (!selectedDrawingType) return;
    if (!chartInstanceRef.current) return; // retried when dataRevision bumps
    if (!selectedSymbol) {
      // DR-06: refuse to enter draw mode with no symbol; drop the request so
      // it does not silently persist an orphaned drawing.
      onDrawingAdded?.();
      return;
    }

    if (requiresTextPrompt(selectedDrawingType)) {
      setShowTextModal(true);
      setTextValue("");
      onDrawingAdded?.();
      return;
    }

    createDrawing(chartInstanceRef.current as Parameters<typeof createDrawing>[0], selectedDrawingType as DrawingInstance["type"], {
      symbol: selectedSymbol.currency,
      chartPanelId,
      addInstance: addDrawing,
      updateSettings: updateDrawingSettings,
    });

    onDrawingAdded?.();
  }, [
    selectedDrawingType,
    selectedSymbol,
    chartPanelId,
    addDrawing,
    updateDrawingSettings,
    chartInstanceRef,
    setShowTextModal,
    setTextValue,
    onDrawingAdded,
    dataRevision,
  ]);

  // Consolidated sync + initial load (DR-04). Previously a separate load effect
  // and this sync effect both created overlays for the same instances on mount
  // and on every symbol switch, double-creating them. `syncDrawings` already
  // treats an instance absent from `stateMap` as a first-time load (it calls
  // `engine.load`), so a single diff-based pass covers both cases. We clear the
  // tracking refs when the symbol changes so a fresh chart starts empty.
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart || !selectedSymbol) return;

    const symbolDrawings = drawings.filter(
      (obj) => obj.symbolKey === selectedSymbol.currency
    );
    const currentIds = new Set(symbolDrawings.map((obj) => obj.id));

    removeStaleOverlays(
      chart as Parameters<typeof removeStaleOverlays>[0],
      trackedOverlayIdsRef.current,
      currentIds,
    );

    const stateMap = syncStateMapRef.current;
    for (const id of [...stateMap.keys()]) {
      if (!currentIds.has(id)) {
        stateMap.delete(id);
      }
    }

    syncDrawings(chart as Parameters<typeof syncDrawings>[0], symbolDrawings, stateMap, updateDrawingSettings);

    symbolDrawings.forEach((obj) => trackedOverlayIdsRef.current.add(obj.id));
  }, [drawings, selectedSymbol, updateDrawingSettings, chartInstanceRef]);

  // Clear tracking when the symbol/timeframe changes — the chart is rebuilt
  // with an empty data list, so any previously tracked overlays are gone.
  useEffect(() => {
    trackedOverlayIdsRef.current.clear();
    syncStateMapRef.current.clear();
  }, [selectedSymbol?.currency, selectedTimeframe, chartPanelId]);

  // Re-anchor future-anchored drawings on every data revision (DR-03).
  //
  // Future-anchored points are rendered via an extrapolated `dataIndex`
  // (see `adjustOverlayPoints`). That index is relative to the last candle, so
  // whenever the data list grows — the initial async OHLC fetch after load, or
  // older history prepended on scroll-back — the index shifts and the drawing
  // drifts. `dataRevision` bumps on every `applyNewData`, so on each bump we
  // reload just the future-anchored instances against the current timing. This
  // also fixes the first-load case (empty data list → clamped positions)
  // without the previous "first revision only" gate.
  useEffect(() => {
    const chart = chartInstanceRef.current as
      | (Parameters<typeof loadDrawings>[0] & {
          getDataList?: () => Array<{ timestamp: number }>;
          removeOverlay: (id: string) => void;
        })
      | null;
    if (!chart || !selectedSymbol) return;

    const dataList = chart.getDataList?.() ?? [];
    if (dataList.length === 0) return;

    const symbolDrawings = getDrawingsBySymbol(selectedSymbol.currency);
    const futureAnchored = symbolDrawings.filter((obj) =>
      isFutureAnchored(
        chart as Parameters<typeof isFutureAnchored>[0],
        obj.settings?.points
      )
    );
    if (futureAnchored.length === 0) return;

    for (const obj of futureAnchored) {
      try {
        chart.removeOverlay(obj.id);
      } catch {
        /* ignore */
      }
      trackedOverlayIdsRef.current.delete(obj.id);
      syncStateMapRef.current.delete(obj.id);
    }

    loadDrawings(
      chart,
      futureAnchored,
      syncStateMapRef.current,
      updateDrawingSettings
    );
    futureAnchored.forEach((obj) => trackedOverlayIdsRef.current.add(obj.id));
  }, [
    dataRevision,
    selectedSymbol,
    selectedTimeframe,
    getDrawingsBySymbol,
    updateDrawingSettings,
    chartInstanceRef,
  ]);
};

/** @deprecated use useChartDrawings */
export const useChartObjects = useChartDrawings;
